import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DateRange } from "@/hooks/useDateRange";
import type { ReviewsSegment } from "@/components/pages/analytics/front-desk/components/reviews/types";
import { SEGMENT_TO_REVIEW_TAB } from "@/components/pages/analytics/front-desk/components/reviews/reviewsSegment";
import {
  getPreviousAnalyticsDateRange,
  toAnalyticsDateParam,
} from "@/components/pages/analytics/front-desk/utils/analyticsDateUtils";
import { analyticsReviewsService } from "@/services/analytics-reviews-service";
import { analyticsTenantService } from "@/services/analytics-tenant-service";
import { partnerDashboardService } from "@/services/partner-dashboard-service";
import type { ReviewOverallResponse } from "@/types/analytics-reviews";
import type {
  ReviewsKpiDeltas,
  ReviewsSummary,
} from "@/components/pages/analytics/front-desk/components/reviews/types";
import {
  computeTenantReviewsKpiDeltas,
  mapReviewsHotelRows,
  mapTenantOverallToSummary,
  mapTenantOverTimeToTrendPoints,
  type AdminReviewsTrendPoint,
} from "../utils/adminReviewsMappers";
import type { AdminHotelMetricRow } from "../types/adminHotelMetricTable";
import { useAdminTenantId } from "./useAdminTenantId";

function isDateRangeReady(dateRange: DateRange): boolean {
  return Boolean(
    toAnalyticsDateParam(dateRange.from) && toAnalyticsDateParam(dateRange.to),
  );
}

async function fetchHotelOverall(
  hotelId: string,
  from: string,
  to: string,
  segment: ReviewsSegment,
): Promise<ReviewOverallResponse | null> {
  try {
    return await analyticsReviewsService.getOverall({
      hotelId,
      from,
      to,
      tab: SEGMENT_TO_REVIEW_TAB[segment],
    });
  } catch {
    return null;
  }
}

async function fetchAdminReviewsData(
  tenantId: string,
  from: string,
  to: string,
  previousFrom: string,
  previousTo: string,
  segment: ReviewsSegment,
) {
  const tab = SEGMENT_TO_REVIEW_TAB[segment];

  const reviewsTable = await analyticsTenantService.getReviewsTable({
    from,
    to,
  });
  const hotels = reviewsTable.rows;

  const [
    tenantOverall,
    previousTenantOverall,
    tenantOverTime,
    currentHotelOverall,
    previousHotelOverall,
    hotelOptions,
  ] = await Promise.all([
    analyticsTenantService.getReviewsOverall({ from, to, tab }),
    analyticsTenantService.getReviewsOverall({
      from: previousFrom,
      to: previousTo,
      tab,
    }),
    analyticsTenantService.getReviewsOverTime({ from, to, tab }),
    Promise.all(
      hotels.map((hotel) =>
        fetchHotelOverall(hotel.hotelId, from, to, segment),
      ),
    ),
    Promise.all(
      hotels.map((hotel) =>
        fetchHotelOverall(hotel.hotelId, previousFrom, previousTo, segment),
      ),
    ),
    partnerDashboardService
      .getHotelFilterOptions(tenantId)
      .catch(
        () =>
          [] as Awaited<
            ReturnType<typeof partnerDashboardService.getHotelFilterOptions>
          >,
      ),
  ]);

  const hotelLogosById = Object.fromEntries(
    hotelOptions.map((hotel) => [hotel.id, hotel.logoId]),
  );

  const currentOverallByHotelId = new Map(
    hotels.map((hotel, index) => [hotel.hotelId, currentHotelOverall[index]]),
  );

  const previousOverallByHotelId = new Map(
    hotels.map((hotel, index) => [hotel.hotelId, previousHotelOverall[index]]),
  );

  return {
    summary: mapTenantOverallToSummary(tenantOverall),
    kpiDeltas: computeTenantReviewsKpiDeltas(
      tenantOverall,
      previousTenantOverall,
    ),
    tableRows: mapReviewsHotelRows(
      hotels,
      currentOverallByHotelId,
      previousOverallByHotelId,
      hotelLogosById,
    ),
    trendPoints: mapTenantOverTimeToTrendPoints(tenantOverTime),
  };
}

export const adminReviewsKeys = {
  all: ["admin-analytics-reviews"] as const,
  hotels: (
    tenantId: string,
    from: string,
    to: string,
    segment: ReviewsSegment,
  ) => [...adminReviewsKeys.all, tenantId, from, to, segment] as const,
};

export function useAdminReviewsHotels(
  dateRange: DateRange,
  segment: ReviewsSegment,
) {
  const tenantId = useAdminTenantId();
  const from = toAnalyticsDateParam(dateRange.from) ?? "";
  const to = toAnalyticsDateParam(dateRange.to) ?? "";
  const previousRange = getPreviousAnalyticsDateRange(dateRange);
  const previousFrom = toAnalyticsDateParam(previousRange?.from) ?? "";
  const previousTo = toAnalyticsDateParam(previousRange?.to) ?? "";
  const isReady = Boolean(
    tenantId && isDateRangeReady(dateRange) && previousFrom && previousTo,
  );

  const query = useQuery({
    queryKey: adminReviewsKeys.hotels(tenantId ?? "", from, to, segment),
    queryFn: () =>
      fetchAdminReviewsData(
        tenantId!,
        from,
        to,
        previousFrom,
        previousTo,
        segment,
      ),
    enabled: isReady,
  });

  const summary = useMemo<ReviewsSummary>(
    () =>
      query.data?.summary ?? {
        overallRating: null,
        positiveNeutralPercent: null,
        negativePercent: null,
        reviewsCount: 0,
      },
    [query.data?.summary],
  );

  const kpiDeltas = useMemo<ReviewsKpiDeltas>(
    () =>
      query.data?.kpiDeltas ?? {
        overallRating: { delta: "—", trend: "neutral" },
        positiveNeutral: { delta: "—", trend: "neutral" },
        negative: { delta: "—", trend: "neutral" },
        reviewsCount: { delta: "—", trend: "neutral" },
      },
    [query.data?.kpiDeltas],
  );

  const tableRows = useMemo<AdminHotelMetricRow[]>(
    () => query.data?.tableRows ?? [],
    [query.data?.tableRows],
  );

  const trendPoints = useMemo<AdminReviewsTrendPoint[]>(
    () => query.data?.trendPoints ?? [],
    [query.data?.trendPoints],
  );

  return {
    tenantId,
    summary,
    kpiDeltas,
    tableRows,
    trendPoints,
    isLoading: query.isLoading,
    isError: query.isError,
    isReady,
  };
}
